import { REACT_FRONTEND_ENABLED } from 'src/config/releaseFeatures'

const REACT_PREFIX = '/react'

/** Where the frontend switch lands when the current page has no twin. */
export const REACT_HOME_PATH = `${REACT_PREFIX}/manage/appels-de-service/list`

interface PathParts {
  pathname: string
  suffix: string
}

function splitPath(fullPath: string): PathParts {
  const suffixIndex = fullPath.search(/[?#]/)
  if (suffixIndex === -1) return { pathname: fullPath, suffix: '' }

  return {
    pathname: fullPath.slice(0, suffixIndex),
    suffix: fullPath.slice(suffixIndex),
  }
}

/** CrudManager answers to a French and an English slug per module; the React routes only know the English one. */
const REACT_MODE_BY_SLUG: Record<string, string> = {
  list: 'list',
  liste: 'list',
  edit: 'edit',
  editer: 'edit',
  detail: 'detail',
  preview: 'preview',
}

function reactServiceCallPath(pathname: string): string | null {
  const match = pathname.match(/^\/manage\/(?:appels-de-service|service-calls)(?:\/([^/]+)(?:\/([^/]+))?)?\/?$/)
  if (!match) return null

  const mode = REACT_MODE_BY_SLUG[match[1] ?? 'list']
  const id = match[2]
  if (!mode || (mode !== 'list' && !id)) return null

  return `${REACT_PREFIX}/manage/appels-de-service/${mode}${id ? `/${id}` : ''}`
}

/** Every client/supplier alias lands on the unified partner workspace, with the old role kept as a filter. */
const PARTNER_LISTER =
  /^\/manage\/(business-partners|partenaires|suppliers|supplier|fournisseurs|fournisseur)(?:\/(list|liste|create|creer|cr%C3%A9er|créer))?\/?$/

function reactPartnerListPath(pathname: string): string | null {
  const match = pathname.match(PARTNER_LISTER)
  if (!match) return null

  const supplier = match[1] !== 'business-partners' && match[1] !== 'partenaires'
  const creating = match[2] !== undefined && match[2] !== 'list' && match[2] !== 'liste'
  const query = new URLSearchParams()
  if (supplier) query.set('role', 'supplier')
  if (creating) query.set('new', '1')
  const suffix = query.toString()
  return `${REACT_PREFIX}/manage/partenaires/list${suffix ? `?${suffix}` : ''}`
}

function quasarPartnerListPath(pathname: string, suffix: string): string | null {
  const match = pathname.match(/^\/manage\/(partenaires|fournisseurs)\/list\/?$/)
  if (!match) return null

  const url = new URL(`https://local.invalid${pathname}${suffix}`)
  const supplier = match[1] === 'fournisseurs' || url.searchParams.get('role') === 'supplier'
  url.searchParams.delete('role')
  const query = url.searchParams.toString()
  return `/manage/${supplier ? 'fournisseurs' : 'partenaires'}/list${query ? `?${query}` : ''}${url.hash}`
}

function quasarServiceCallPath(pathname: string): string | null {
  const match = pathname.match(/^\/manage\/appels-de-service\/(list|edit|detail|preview)(?:\/([^/]+))?\/?$/)
  if (!match) return null

  const mode = match[1]
  const id = match[2]
  if (mode === 'list') return '/manage/appels-de-service/list'
  if (!id) return null

  return `/manage/appels-de-service/edit/${id}`
}

function reactPartsPath(pathname: string): string | null {
  const match = pathname.match(/^\/manage\/(?:parts|pi%C3%A8ces|pièces)(?:\/(list|liste|create|creer|cr%C3%A9er|créer|edit|editer)(?:\/([^/]+))?)?\/?$/i)
  if (!match) return null

  const mode = match[1]
  const id = match[2]
  if (mode === 'edit' || mode === 'editer') {
    if (!id) return null
    return `${REACT_PREFIX}/dashboard/parts/${id}`
  }

  const query = new URLSearchParams()
  if (mode && !['list', 'liste'].includes(mode)) query.set('new', '1')
  const suffix = query.toString()
  return `${REACT_PREFIX}/dashboard/parts${suffix ? `?${suffix}` : ''}`
}

function quasarPartsPath(pathname: string): string | null {
  const match = pathname.match(/^\/dashboard\/parts(?:\/([^/]+))?\/?$/)
  if (!match) return null

  const id = match[1]
  if (id) return `/manage/parts/edit/${id}`
  return '/manage/parts/list'
}

export function toReactVersionPath(fullPath: string): string | null {
  if (!REACT_FRONTEND_ENABLED) return null
  const { pathname, suffix } = splitPath(fullPath)
  if (pathname.startsWith(`${REACT_PREFIX}/`) || pathname === REACT_PREFIX) return null

  const serviceCallPath = reactServiceCallPath(pathname)
  if (serviceCallPath) return `${serviceCallPath}${suffix}`

  const partnerListPath = reactPartnerListPath(pathname)
  if (partnerListPath) return partnerListPath.includes('?') ? `${partnerListPath}${suffix.replace(/^\?/, '&')}` : `${partnerListPath}${suffix}`

  const partsPath = reactPartsPath(pathname)
  if (partsPath) return partsPath.includes('?') ? `${partsPath}${suffix.replace(/^\?/, '&')}` : `${partsPath}${suffix}`

  if (/^\/clients\/[^/]+\/?$/.test(pathname) || /^\/dashboard\/finances\/?$/.test(pathname)) {
    return `${REACT_PREFIX}${pathname}${suffix}`
  }

  // Legacy classic dashboard bookmarks follow the React island.
  if (/^\/dashboard\/parts(?:\/[^/]+)?\/?$/.test(pathname)) {
    return `${REACT_PREFIX}${pathname}${suffix}`
  }

  return null
}

export function toQuasarVersionPath(fullPath: string): string | null {
  const { pathname, suffix } = splitPath(fullPath)
  if (!pathname.startsWith(`${REACT_PREFIX}/`)) return null

  const unprefixed = pathname.slice(REACT_PREFIX.length)
  const serviceCallPath = quasarServiceCallPath(unprefixed)
  if (serviceCallPath) return `${serviceCallPath}${suffix}`

  const partnerListPath = quasarPartnerListPath(unprefixed, suffix)
  if (partnerListPath) return partnerListPath

  const partsPath = quasarPartsPath(unprefixed)
  if (partsPath) return `${partsPath}${suffix}`

  if (/^\/clients\/[^/]+\/?$/.test(unprefixed) || /^\/dashboard\/finances\/?$/.test(unprefixed)) {
    return `${unprefixed}${suffix}`
  }

  return null
}
